import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/WorkPreferences.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

export default function NotificationPreferences() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");

  const [notificationPreference, setNotificationPreference] = useState({
    email: false,
    sms: false,
    whatsapp: false,
  });

  // Translations
  const t = {
    en: {
      generalSettings: "General Settings",
      pageTitle: "Notification Preferences",
      email: "Email",
      whatsapp: "WhatsApp",
      sms: "SMS",
      save: "Save",
      saving: "Saving...",
      loading: "Loading",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      pageTitle: "تفضيلات الإشعارات",
      email: "البريد الإلكتروني",
      whatsapp: "واتساب",
      sms: "رسالة نصية",
      save: "حفظ",
      saving: "جاري الحفظ...",
      loading: "جاري التحميل",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;
          
          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);
          
          // Populate form fields
          setNotificationPreference({
            email: profileData.notification_preference?.email || false,
            sms: profileData.notification_preference?.sms || false,
            whatsapp: profileData.notification_preference?.whatsapp || false,
          });
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        showToast(err.message || "Failed to load profile", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle save
  const handleSave = async () => {
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("notification_preference", JSON.stringify(notificationPreference));

      const response = await fetch(
        `${USER_BASE_URL}/${VERSION}/profile/edit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast(
          lang === "ar" ? "تم الحفظ بنجاح" : "Saved successfully",
          "success"
        );
        // Refresh page
        window.location.reload();
      } else {
        showToast(
          data?.message || (lang === "ar" ? "فشل الحفظ" : "Failed to save"),
          "error"
        );
        setSaving(false);
      }
    } catch (err) {
      console.error("Error saving work preferences:", err);
      showToast(
        err.message || (lang === "ar" ? "فشل الحفظ" : "Failed to save"),
        "error"
      );
      setSaving(false);
    }
  };

  // Handle notification preference change
  const handleNotificationChange = (type) => {
    setNotificationPreference((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Loading screen
  if (loading) {
    return (
      <div className="work-preferences-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="work-preferences-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header with Back Button */}
      <div className="work-preferences-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="work-preferences-page-title">{t.generalSettings}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="work-preferences-content">
        {/* Notification Preferences Section */}
        <div className="work-preferences-section">
          <h2 className="section-title">{t.pageTitle}</h2>
          
          <div className="notification-checkboxes">
            {/* Email */}
            <div className="checkbox-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPreference.email}
                  onChange={() => handleNotificationChange("email")}
                  disabled={saving}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t.email}</span>
              </label>
            </div>

            {/* WhatsApp */}
            <div className="checkbox-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPreference.whatsapp}
                  onChange={() => handleNotificationChange("whatsapp")}
                  disabled={saving}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t.whatsapp}</span>
              </label>
            </div>

            {/* SMS */}
            <div className="checkbox-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPreference.sms}
                  onChange={() => handleNotificationChange("sms")}
                  disabled={saving}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t.sms}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          className="work-preferences-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="save-spinner"></span>
              {t.saving}
            </>
          ) : (
            t.save
          )}
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

